import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  AOS_V1_SCHEMA_VERSION,
  aosV1FieldDefinitions,
  type FieldDefinition,
  type FieldId,
} from '@inside-us-aos/domain';

import {
  createFormEngine,
  type AnswerPersistenceAdapter,
  type FormEngine,
} from '@inside-us-aos/form-engine';
import {
  type IParentGroupView,
  type ISectionProgressView,
  type IUploadedFileMetadata,
  SectionProgressTree,
} from '@inside-us-aos/ui';

import {
  type DownloadStatus,
  injectSystemAnswers,
  useDownloadFilledForm,
} from '@inside-us-aos/pdf-engine-react';

import styles from './App.module.css';
import { SectionValidationSummary } from './SectionValidationSummary';
import { createLocalStoragePersistenceAdapter } from './persistence';
import { reportError, reportEvent } from './monitoring';
import { reportBackgroundWriteFailure } from './firebase/backgroundWriteReporting';
import { AuditPanel } from './AuditPanel';
import {
  applyProjectionsToEngine,
  loadScreenerHandoff,
  projectScreenerAnswersToCatalog,
} from './screener/screenerHandoff';
import {
  ORDERED_SECTIONS,
  PARENT_GROUPS,
  getFieldsForSection,
  getSectionDisplayLabel,
  getSummaryLeafIdToParentId,
} from './intake/sections';
import { SectionRenderer } from './intake/SectionRenderer';
import { ParentReviewPanel } from './intake/ParentReviewPanel';
import { WelcomeBackBanner } from './intake/WelcomeBackBanner';
import { logCatalogGapSummary } from './intake/discoveries';
import { useTabPresence } from './intake/useTabPresence';
import { stringifyAnswer } from './intake/stringifyAnswer';
import { resolveFieldOptions } from './intake/optionsResolver';
import {
  getLastSectionId,
  setLastSectionId,
} from './intake/lastSectionStorage';
import {
  defaultCaseStateForUnknown,
  isPostSubmitState,
  type TCaseState,
} from './intake/caseState';
import { PostSubmitView } from './intake/PostSubmitView';

// Surface the catalog-gap audit to console once
// per session in development. Production builds drop the warn via
// dead-code elimination because `import.meta.env.DEV` is statically
// false. Tests inherit DEV=true so the warning fires during render;
// vitest swallows console output unless a test asserts on it.
let _phase7aDiscoveriesEmitted = false;
function emitPhase7aDiscoveriesOnce(): void {
  if (_phase7aDiscoveriesEmitted) return;
  _phase7aDiscoveriesEmitted = true;
  if (
    typeof import.meta !== 'undefined' &&
    (import.meta as { env?: { DEV?: boolean } }).env?.DEV
  ) {
    logCatalogGapSummary();
  }
}

const fieldsById = new Map<FieldId, FieldDefinition>(
  aosV1FieldDefinitions.map(field => [field.id, field]),
);

// Anchor field-ids the App reads directly for top-bar labels and
// download-button gating. Each is asserted to exist in the catalog at
// module load — a missing id is a catalog regression and surfaces
// loudly at startup.
function getCatalogField(id: string): FieldDefinition {
  const field = fieldsById.get(id as FieldId);
  if (!field) {
    throw new Error(
      `App expected catalog field "${id}" to exist; check the catalog.`,
    );
  }
  return field;
}

const FIRST_NAME_FIELD = getCatalogField('beneficiary.name.legal.first');
const LAST_NAME_FIELD = getCatalogField('beneficiary.name.legal.last');
const SPONSOR_DAYTIME_PHONE_FIELD = getCatalogField(
  'sponsor.contact.daytime_phone',
);

/**
 * Subscribes to the form engine and bumps a counter on each answer
 * change so React re-renders.
 */
function useEngineRevision(engine: FormEngine): number {
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    return engine.subscribe(() => setRevision(prev => prev + 1));
  }, [engine]);
  return revision;
}

function readText(engine: FormEngine, id: FieldId): string {
  const value = engine.getAnswer(id);
  return value && value.type === 'text' ? value.value : '';
}

/**
 * Phase 7e — humanises the millisecond gap between `nowMs` and a
 * recorded save timestamp into a short "a few seconds ago" / "N
 * minutes ago" label for the top-bar auto-save indicator. Uses
 * `Intl.RelativeTimeFormat` with a curated set of buckets — minutes
 * and hours are the only resolutions that matter here, and we floor
 * sub-minute deltas to "a few seconds ago" rather than counting
 * exact seconds (the indicator should feel reassuring, not a clock).
 */
function formatRelativeSave(savedAtMs: number, nowMs: number): string {
  // Returns just the relative-time
  // fragment ("a few seconds ago", "5 minutes ago"). The autosave
  // indicator now renders the wording as
  // `<icon> Saved · {formatRelativeSave(...)}` — pulling the
  // "Auto-saved" prefix out of this helper lets the JSX own the
  // reassurance copy alongside the green check, while keeping the
  // formatter pure (just time math).
  const deltaMs = Math.max(0, nowMs - savedAtMs);
  const seconds = Math.floor(deltaMs / 1000);
  if (seconds < 45) return 'a few seconds ago';
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  if (minutes < 60) {
    return rtf.format(-Math.max(1, minutes), 'minute');
  }
  return rtf.format(-Math.max(1, hours), 'hour');
}

/**
 * Optional dependencies the App accepts. Defaults wire the in-browser
 * demo (localStorage adapter, in-memory file metadata). The Firebase
 * wrapper passes Firestore + Cloud Storage adapters from main.tsx.
 */
export interface IAppProps {
  /** Defaults to `'demo-application'`. */
  applicationId?: string;
  /** Override the persistence adapter (default: localStorage). */
  persistenceAdapter?: AnswerPersistenceAdapter | null;
  /**
   * Hook that uploads a file when the user picks one in the
   * Documents section. When omitted, the dispatcher's
   * DocumentUpload mount falls back to in-memory placeholder ids.
   */
  uploadDocument?: (
    fieldId: FieldId,
    file: IUploadedFileMetadata,
  ) => Promise<{ uploadedFileId: string }>;
  topBarSlot?: ReactNode;
  updatedBy?: string;
  connectAudit?: (source: {
    subscribeAudit: FormEngine['subscribeAudit'];
  }) => { disconnect: () => Promise<void> | void };
  /**
   * Phase 8 (applicant-web slice) — current case state from the
   * Firestore application doc. When in `POST_SUBMIT_STATES`, the App
   * mounts `<PostSubmitView>` instead of the editable intake. Legacy
   * applications (no `caseState` field) default to `'filling'` so
   * existing applicants keep their intake mid-session.
   */
  caseState?: TCaseState;
  /** ms-epoch the application was submitted (Firestore submittedAt). */
  submittedAtMs?: number | null;
  /** ms-epoch the case team marked the packet shipped, if known. */
  shippedAtMs?: number | null;
  /** Carrier tracking number captured at shipment time, if available. */
  trackingNumber?: string | null;
  /**
   * Storage path of the latest assembled packet PDF. When set, the
   * post-submit view renders a "Your filing packet" download
   * section, regardless of caseState — case team may upload during
   * `docket_ready` and applicant should see it immediately.
   */
  packetStoragePath?: string;
  /** Original filename for the packet download UX. */
  packetFileName?: string;
  /** Bytes — used in the "12.4 MB" display next to the filename. */
  packetSizeBytes?: number;
  /**
   * Click handler that resolves a signed URL for the packet and
   * opens it in a new tab. Lazy on click — URLs expire, so the
   * FirebaseApp wiring must NOT pre-fetch on mount.
   */
  onDownloadPacket?: () => Promise<void>;
  /**
   * Phase 8 (applicant-web slice) — submit-for-review handler. The
   * SubmitForReviewPage (mounted when the user lands on the
   * `signatures` section) invokes this when the applicant clicks
   * "Submit for review →" with both confirmations checked. The
   * FirebaseApp wrapper wires this to the firebase-adapter's
   * `submitApplication()` helper, which writes
   * `caseState: 'in_review'` + `submittedAt` to the Firestore
   * application doc. After it lands, the existing caseState routing
   * (this file's `isPostSubmitState` branch) swaps in
   * `<PostSubmitView>`.
   *
   * Omitted in the localStorage demo and most tests — the
   * SubmitForReviewPage falls back to a no-op submit so the surface
   * still renders.
   */
  onSubmitForReview?: () => Promise<void>;
  /**
   * When `true`, the Signatures section exposes Download-your-form
   * buttons that run the PDF engine locally. Paying customers MUST
   * NOT see these — the deliverable is what they pay Greenbroad to
   * assemble. Provisioned via the `tester: true` Firebase custom
   * claim; the `FirebaseApp` wrapper resolves the claim and threads
   * it through. Defaults to `false` so the gated UI never leaks even
   * if the wrapper's claim resolver fails open.
   */
  isTester?: boolean;
}

export function App(props: IAppProps = {}): JSX.Element {
  emitPhase7aDiscoveriesOnce();
  const applicationId = props.applicationId ?? 'demo-application';
  const updatedBy = props.updatedBy ?? 'phase-7a-catalog-driven';

  const engine = useMemo(
    () =>
      createFormEngine(aosV1FieldDefinitions, {
        applicationId,
        schemaVersion: AOS_V1_SCHEMA_VERSION,
        updatedBy,
        // Coalesce window is the safety net: if the user never
        // navigates and never closes the tab, audit events still
        // eventually flush after this many ms of inactivity per
        // (field, instance). The primary flush trigger is now
        // section navigation — see `engine.flushPendingAudit()`
        // wired into `setCurrentSectionId` and the page-hide
        // listener below. Production saw 214k audit rows on a
        // single draft when this window was 1s (and replay also
        // emitted); 60s + section flush keeps the trail to one
        // event per (field, instance) per section visit.
        coalesceMs: 60_000,
      }),
    [applicationId, updatedBy],
  );

  // Phase 7i — keep a handle on the revision counter so derived views
  // below can memoize against it. Bumps once per engine answer change.
  const engineRevision = useEngineRevision(engine);

  // Persistence adapter resolution (unchanged from Phase 6).
  const fallbackAdapter = useMemo(() => {
    try {
      return createLocalStoragePersistenceAdapter();
    } catch {
      return null;
    }
  }, []);
  const persistenceAdapter =
    props.persistenceAdapter !== undefined
      ? props.persistenceAdapter
      : fallbackAdapter;

  // Cross-tab presence guard. When the same applicationId
  // is open in another tab, the lower tabId wins primary status; the
  // others become secondary and pause auto-save so they can't
  // overwrite each other's edits via the 500ms debounced persist.
  const { isPrimary, peerCount } = useTabPresence(applicationId);

  const [hasHydrated, setHasHydrated] = useState(false);
  // Surface hydrate errors to the user instead of silently
  // flipping `hasHydrated` to true. When hydrate fails (network blip,
  // Firestore permission error, schema-version mismatch), we still
  // let the form render so the user isn't stuck on a skeleton, but
  // we show a non-blocking banner that says "couldn't load your
  // saved work" with a Retry button. While in this state, the
  // debounced persist effect below also shorts out so we don't
  // overwrite legitimate saved data with an empty new draft.
  const [hydrateError, setHydrateError] = useState<Error | null>(null);
  const [hydrateAttempt, setHydrateAttempt] = useState(0);
  // Defer the hydrate skeleton.
  // See full rationale at the early-return below.
  const [showHydrateSkeleton, setShowHydrateSkeleton] = useState(false);
  useEffect(() => {
    if (hasHydrated) {
      setShowHydrateSkeleton(false);
      return;
    }
    const handle = setTimeout(() => setShowHydrateSkeleton(true), 80);
    return () => clearTimeout(handle);
  }, [hasHydrated]);

  useEffect(() => {
    if (!persistenceAdapter) {
      setHasHydrated(true);
      return;
    }
    let cancelled = false;
    void engine
      .hydrate(persistenceAdapter, applicationId)
      .then(() => {
        if (!cancelled) {
          setHydrateError(null);
          setHasHydrated(true);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const error =
          err instanceof Error ? err : new Error(String(err ?? 'unknown'));
        // Hydrate failure means we couldn't load the saved snapshot
        // from Firestore. The user always sees the banner + Retry
        // below. For Sentry, a transient offline hydrate is expected
        // noise (breadcrumb); a real failure pages as an error.
        reportBackgroundWriteFailure(error, {
          kind: 'persistence',
          flow: 'hydrate',
          applicationId,
        });
        setHydrateError(error);
        // Still flip hasHydrated so the form renders and the user
        // isn't stuck on a permanent skeleton — the banner below
        // tells them their work won't auto-save and offers Retry.
        setHasHydrated(true);
      });
    return () => {
      cancelled = true;
    };
  }, [engine, persistenceAdapter, applicationId, hydrateAttempt]);

  // Screener handoff merge.
  //
  // Sentry MCP triage caught earlier
  // versions of this loop crashing on `eligibility.organization_
  // memberships.*` fields after the catalog made them repeatable.
  // engine.getAnswer/setAnswer throw "requires a groupInstanceId"
  // for repeatable fields, and the throw bubbles up the React tree
  // to the SentryErrorBoundary, replacing the whole page with the
  // fallback UI. Today's `projectScreenerAnswersToCatalog` doesn't
  // target any repeatable fields, but the contract is fragile —
  // any future projection mistake would crash the sign-up page.
  // Forward defense: catch the throw, log via reportError, and
  // continue with the next projection so a single bad projection
  // never takes down the page.
  useEffect(() => {
    if (!hasHydrated) return;
    const handoff = loadScreenerHandoff();
    if (!handoff) return;
    const projections = projectScreenerAnswersToCatalog(handoff.answers);
    applyProjectionsToEngine(engine, projections, (err, ctx) => {
      reportError(err, {
        where: 'screener-handoff-projection',
        ...ctx,
      });
    });
  }, [engine, hasHydrated]);

  // Phase 7e — track the wall-clock timestamp of the most recent
  // successful persist so the top-bar "Auto-saved …" indicator can
  // tell the user their work is safe. Updates after the debounced
  // persist resolves (not per-keystroke); a tiny ticking effect
  // re-renders so "a few seconds ago" advances on its own.
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  // Debounced auto-save. Phase 7e — record the persist timestamp so
  // the top bar can echo "Auto-saved a few seconds ago" without the
  // applicant doubting whether their work is safe.
  //
  // Short-circuit while a hydrate error is outstanding.
  // The user is editing a freshly-empty engine state; persisting it
  // would overwrite their legitimate saved snapshot with a blank
  // draft. They'll see the banner and can Retry hydrate to recover.
  useEffect(() => {
    if (!persistenceAdapter || !hasHydrated || hydrateError !== null) return;
    // Non-primary tabs (i.e., the same application is
    // open in another tab elected as primary) skip auto-save so they
    // can't overwrite the primary tab's edits.
    if (!isPrimary) return;
    const timeout = { current: null as ReturnType<typeof setTimeout> | null };
    const unsubscribe = engine.subscribe(() => {
      if (timeout.current !== null) clearTimeout(timeout.current);
      timeout.current = setTimeout(() => {
        void engine
          .persist(persistenceAdapter, applicationId)
          .then(() => setLastSavedAt(Date.now()))
          .catch((err: unknown) => {
            // Auto-save failures are user-invisible. A transient
            // offline blip is expected (the SDK queues the write and
            // replays on reconnect) → breadcrumb; a persistent broken
            // session (token expired, rules misconfigured) → error.
            // We keep the prior `lastSavedAt` so the top-bar indicator
            // doesn't lie about a save completing.
            reportBackgroundWriteFailure(err, {
              kind: 'persistence',
              flow: 'autosave',
              applicationId,
            });
          });
      }, 500);
    });
    return () => {
      if (timeout.current !== null) clearTimeout(timeout.current);
      unsubscribe();
    };
  }, [
    engine,
    persistenceAdapter,
    applicationId,
    hasHydrated,
    hydrateError,
    isPrimary,
  ]);

  // Cross-tab sync — disabled (2026-05-02). The Firestore onSnapshot
  // listener fired on every server-acked update INCLUDING reconnect
  // events. Each fire called engine.hydrate(), which reads the
  // persisted snapshot and replaces local engine state. That raced
  // catastrophically with the 500ms debounced auto-save: a user
  // would click a Yes/No radio, see Yes briefly, then see it snap
  // back to the persisted (older) value because hydrate fired before
  // the new value reached Firestore. Result: every form interaction
  // appeared to undo itself.
  //
  // Removing the listener stops the data loss. Multi-tab editing of
  // the same application is not currently a use case (single
  // applicant + sponsor share one account). When/if cross-tab sync
  // is needed, it requires conflict resolution: don't replace local
  // state when there are unpersisted changes; merge instead of
  // replace; or queue local changes and flush them before applying
  // remote.

  // Audit writer wiring (unchanged).
  const connectAudit = props.connectAudit;
  useEffect(() => {
    if (!connectAudit) return;
    const conn = connectAudit({ subscribeAudit: engine.subscribeAudit });
    return () => {
      void conn.disconnect();
    };
  }, [connectAudit, engine]);

  // Active section in the sidebar.
  //
  // Phase 7e — initialize from `localStorage` so a returning applicant
  // lands on the section they were last editing instead of being
  // bounced back to `ORDERED_SECTIONS[0]`. The stored value is
  // accepted only when it matches a real catalog section or one of
  // the reserved `__review_*` summary-leaf ids; anything else falls
  // back to the first section so a stale or hand-edited storage entry
  // can't strand the user on a non-existent destination.
  const [currentSectionId, setCurrentSectionIdState] = useState<string>(
    () => {
      const stored = getLastSectionId(applicationId);
      if (stored === null) return ORDERED_SECTIONS[0];
      const orderedSet = new Set<string>(ORDERED_SECTIONS);
      if (orderedSet.has(stored)) return stored;
      const reviewLeafIds = getSummaryLeafIdToParentId();
      if (reviewLeafIds.has(stored)) return stored;
      return ORDERED_SECTIONS[0];
    },
  );
  // Track sections the user has actually navigated to. The progress
  // tree uses this to suppress the orange "gaps" dot on sections the
  // user hasn't visited yet — the screener handoff seeds a few
  // answers (e.g., into Immigration History, Eligibility) which
  // flips engine status to `in_progress` before the user gets there,
  // and showing orange on first login reads as "you arrived broken."
  // Initialized with the current section since the user is, by
  // definition, viewing it on first render.
  const [visitedSectionIds, setVisitedSectionIds] = useState<
    ReadonlySet<string>
  >(() => new Set([currentSectionId]));
  const setCurrentSectionId = (next: string): void => {
    // Force-flush the audit buffer at every section boundary so the
    // audit trail records one event per (field, instance) per
    // section visit instead of waiting for the 60s safety-net
    // timer. Without this, switching sections quickly would let a
    // pending bucket sit through the whole next section before
    // emitting.
    engine.flushPendingAudit();
    setCurrentSectionIdState(next);
    setLastSectionId(applicationId, next);
    setVisitedSectionIds(prev => {
      if (prev.has(next)) return prev;
      const out = new Set(prev);
      out.add(next);
      return out;
    });
  };

  // Scroll to top of page when the
  // current section changes (Continue / Back / left-tree click /
  // top-bar Continue → next-required-field jump). Without this, the
  // browser kept the prior section's scroll position so users
  // frequently landed mid-page on the new section. `behavior: 'auto'`
  // (instant) feels right for SPA navigation between "pages" — smooth
  // scroll on every nav reads as animation overhead.
  //
  // Skipping the initial mount: only scrolls when currentSectionId
  // ACTUALLY changes after the first render. Prevents the page from
  // snap-scrolling to top when an applicant resumes a session at
  // their last-edited section (the persisted resume scroll, if any,
  // would be overridden).
  const initialSectionRef = useRef(currentSectionId);
  useEffect(() => {
    if (currentSectionId === initialSectionRef.current) return;
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [currentSectionId]);

  // Force-flush the audit buffer when the user backgrounds the tab
  // or closes the page. visibilitychange is the reliable signal
  // across browsers (beforeunload doesn't fire on mobile Safari /
  // PWAs). Without this, a partially-typed answer that's still
  // sitting in a coalesce bucket would be lost from the audit log
  // when the user closes the laptop or switches tabs.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') engine.flushPendingAudit();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [engine]);

  // Phase 7e — in-memory dismissal for the "Welcome back" banner. The
  // applicant is, definitionally, a returning user every time they
  // come back to the app, so we don't persist this dismissal across
  // sessions; a per-mount toggle is enough to honour "Got it" for the
  // remainder of the current visit.
  const [welcomeBackDismissed, setWelcomeBackDismissed] = useState(false);

  // Section completion (Phase 7e expansion).
  const [completedSections, setCompletedSections] = useState<
    ReadonlySet<string>
  >(new Set());

  // Touched-key set per Phase 7b. Singleton fields key on FieldId;
  // repeatable fields key on `${fieldId}#${instanceId}`.
  const [touchedFieldIds, setTouchedFieldIds] = useState<
    ReadonlySet<string>
  >(new Set());
  const markFieldTouched = (key: string): void => {
    setTouchedFieldIds(prev => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  // Repeatable instance state — keyed by groupId. Phase 7e Slice
  // promotes this to a Redux slice + persistence; Phase 7a holds it
  // in component state so the dispatcher has something to drive.
  const [instancesByGroup, setInstancesByGroup] = useState<
    Map<string, string[]>
  >(new Map());

  // Upload error surface — populated when uploadDocument throws.
  const [uploadError, setUploadError] = useState<string | null>(null);
  const onAddInstance = (groupId: string): void => {
    setInstancesByGroup(prev => {
      const list = prev.get(groupId) ?? [];
      const next = new Map(prev);
      next.set(groupId, [
        ...list,
        `${groupId}-${list.length + 1}-${Date.now()}`,
      ]);
      return next;
    });
  };
  const onRemoveInstance = (groupId: string, instanceId: string): void => {
    setInstancesByGroup(prev => {
      const list = prev.get(groupId) ?? [];
      const next = new Map(prev);
      next.set(
        groupId,
        list.filter(id => id !== instanceId),
      );
      return next;
    });
  };

  // Auto-seed a first row whenever the applicant or sponsor answers
  // "Yes, I've used other names" and the matching repeatable group is
  // still empty. Before this, the user picked Yes and had to click
  // "Add row" — the count input that used to live here (deleted from
  // the catalog this slice) advertised the right answer but never
  // actually drove the rows. The repeatable group is now the single
  // source of truth: Yes = at least one row appears immediately.
  //
  // Effect depends on `engineRevision` so it re-runs when the boolean
  // flips. We guard on instances.length === 0 so a user who fills
  // their one row, removes it, and answers Yes again doesn't get a
  // brand-new auto-seeded blank — the empty state means "the user
  // hasn't entered anything yet," which is exactly when we want to
  // seed.
  useEffect(() => {
    const pairs: ReadonlyArray<readonly [FieldId, string]> = [
      ['beneficiary.name.other.has_used' as FieldId, 'beneficiary.other_names'],
      ['sponsor.name.other.has_used' as FieldId, 'sponsor.other_names'],
    ];
    for (const [hasUsedId, groupId] of pairs) {
      const answer = engine.getAnswer(hasUsedId);
      if (answer?.type !== 'boolean' || answer.value !== true) continue;
      const existing = instancesByGroup.get(groupId) ?? [];
      if (existing.length > 0) continue;
      onAddInstance(groupId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- engineRevision keys engine state
  }, [engine, engineRevision, instancesByGroup]);

  // Auto-scaffold child rows from the `beneficiary.children.count`
  // number answer. Before this, users typed "3" in the count field
  // and then had to manually click "Add row" three times — confusing
  // since the question implied the rows would appear automatically.
  //
  // Semantics: fire ONLY when count INCREASES (we track the last-
  // seen count in a ref). Removing a row manually never triggers a
  // re-add because the count value didn't change. Lowering the count
  // never auto-removes rows (the user may have drafted data on the
  // rows; manual Remove buttons are the only delete path).
  //
  // Capped at the catalog bound max (8) to honour the I-485 / I-130
  // child-slot count.
  const lastSeenChildrenCountRef = useRef<number | null>(null);
  useEffect(() => {
    const countId = 'beneficiary.children.count' as FieldId;
    const groupId = 'beneficiary.children';
    const answer = engine.getAnswer(countId);
    if (answer?.type !== 'number') {
      lastSeenChildrenCountRef.current = null;
      return;
    }
    const target = Math.min(Math.max(0, answer.value), 8);
    const lastSeen = lastSeenChildrenCountRef.current;
    lastSeenChildrenCountRef.current = target;
    // Only act when the count actually went UP since last evaluation.
    if (lastSeen !== null && target <= lastSeen) return;
    let addedCount = 0;
    setInstancesByGroup(prev => {
      const current = prev.get(groupId) ?? [];
      if (current.length >= target) return prev;
      const next = new Map(prev);
      const additions: string[] = [];
      for (let i = 0; i < target - current.length; i += 1) {
        additions.push(`${groupId}-${current.length + i + 1}-${Date.now()}-${i}`);
      }
      addedCount = additions.length;
      next.set(groupId, [...current, ...additions]);
      return next;
    });
    if (addedCount > 0) {
      // Breadcrumb so a subsequent Sentry issue shows that the user
      // typed N in the count field and N rows were auto-scaffolded.
      reportEvent('intake.children_auto_added', {
        added: addedCount,
        target,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- engineRevision keys engine state
  }, [engine, engineRevision]);

  const markSectionComplete = (sectionId: string): void => {
    const progress = engine.getAllSectionProgress().get(sectionId);
    if (progress && progress.missingFieldIds.length > 0) {
      setTouchedFieldIds(prev => {
        const next = new Set(prev);
        for (const id of progress.missingFieldIds) {
          next.add(id);
        }
        return next;
      });
      return;
    }
    setCompletedSections(prev => {
      if (prev.has(sectionId)) return prev;
      const next = new Set(prev);
      next.add(sectionId);
      return next;
    });
  };

  // Download wiring — injectSystemAnswers stamps I-864 household-size
  // constants and derived sums into the answer set on every download so
  // the PDF engine always gets consistent values without requiring the
  // user to manually enter them.
  const downloadApi = useDownloadFilledForm({
    catalog: aosV1FieldDefinitions,
    getAnswers: () =>
      injectSystemAnswers(engine.getAllAnswers(), instancesByGroup, applicationId),
    reporter: { reportError, reportEvent },
  });
  const hasAnyAnswer = engine.getAllAnswers().length > 0;
  const hasSponsorPhone =
    readText(engine, SPONSOR_DAYTIME_PHONE_FIELD.id).length > 0;

  // Project per-section progress into the sidebar primitive's view.
  // We deliberately call `getAllSectionProgress()` on every render
  // rather than memoize it — the underlying computation is cheap
  // (O(N=580) over already-touched answers) and several test seams
  // (topBarReadiness, App readiness flips) replace this method on the
  // engine instance to drive deterministic rerender scenarios.
  const sectionProgress = engine.getAllSectionProgress();
  // Memoized on the engine revision so the array is referentially
  // stable across unrelated renders. Without this, downstream
  // consumers (SectionProgressTree's auto-expand effect, in
  // particular) saw a fresh array reference every render and
  // re-fired their effects, fighting the user's manual interactions
  // (clicked-to-expand parents collapsed back on the next render).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sectionViews = useMemo<readonly ISectionProgressView[]>(() => {
    return ORDERED_SECTIONS.map(sectionId => {
      const title = getSectionDisplayLabel(sectionId);
      const progress = sectionProgress.get(sectionId);
      if (!progress) {
        return {
          id: sectionId,
          title,
          answered: 0,
          required: 0,
          percentComplete: 100,
          status: 'complete' as const,
        };
      }
      return {
        id: sectionId,
        title,
        answered: progress.answered,
        required: progress.required,
        percentComplete: progress.percentComplete,
        status: progress.status,
      };
    });
  }, [engine, engineRevision, completedSections]);

  // Derive 2-level parent-group views by joining
  // PARENT_GROUPS (the structural metadata from intake/sections.ts)
  // with `sectionViews` (the engine-driven per-section progress
  // payload). Sections referenced by a parent that haven't yet been
  // observed in `sectionViews` are filtered out so the tree never
  // renders a stale or absent section.
  //
  // Phase 7i — the expensive part of this derivation is
  // `sectionHasVisibleFields` (one `engine.getFieldStatus` call per
  // catalog field per nav section). Before this memo it ran ≈30 nav
  // sections × 580 fields = ~17K iterations on every App render. We
  // now memoize the visible-set keyed on the engine revision (the only
  // thing that affects visibility) and apply the cheap section-view
  // join in plain code below.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- engineRevision keys engine state
  const visibleSectionIds = useMemo<ReadonlySet<string>>(() => {
    const out = new Set<string>();
    for (const group of PARENT_GROUPS) {
      const sectionIds = group.subHeadings
        ? group.subHeadings.flatMap(s => Array.from(s.sectionIds))
        : Array.from(group.sectionIds ?? []);
      for (const id of sectionIds) {
        if (out.has(id)) continue;
        const fields = getFieldsForSection(id);
        for (const f of fields) {
          if (engine.getFieldStatus(f.id).visible) {
            out.add(id);
            break;
          }
        }
      }
    }
    return out;
  }, [engine, engineRevision]);

  // Memoized for referential stability — same reasoning as
  // sectionViews above. SectionProgressTree's auto-expand-on-current-
  // section effect depends on `parentGroups` reference equality.
  const sectionViewsById = useMemo(
    () =>
      new Map<string, ISectionProgressView>(sectionViews.map(v => [v.id, v])),
    [sectionViews],
  );
  const parentGroupViews = useMemo<readonly IParentGroupView[]>(
    () =>
      PARENT_GROUPS.map(group => {
        if (group.subHeadings) {
          return {
            id: group.id,
            label: group.label,
            subHeadings: group.subHeadings.map(sub => ({
              label: sub.label,
              sections: sub.sectionIds
                .filter(id => visibleSectionIds.has(id))
                .map(id => sectionViewsById.get(id))
                .filter((v): v is ISectionProgressView => v !== undefined),
            })),
            summaryLeaf: group.summaryLeaf,
          };
        }
        return {
          id: group.id,
          label: group.label,
          sections: (group.sectionIds ?? [])
            .filter(id => visibleSectionIds.has(id))
            .map(id => sectionViewsById.get(id))
            .filter((v): v is ISectionProgressView => v !== undefined),
          summaryLeaf: group.summaryLeaf,
        };
      }),
    [sectionViewsById, visibleSectionIds],
  );

  // When `currentSectionId` is a reserved
  // `__review_*` pseudo id, mount <ParentReviewPanel> instead of
  // <SectionRenderer>. The leaf is OPTIONAL — there is no gate before
  // "Print & mail"; users can ignore it.
  const summaryLeafToParent = getSummaryLeafIdToParentId();
  const summaryLeafParentId = summaryLeafToParent.get(currentSectionId);
  const isSummaryLeaf = summaryLeafParentId !== undefined;

  // Readiness state replaces "% complete" in the
  // top bar. We sum required-visible-unfilled fields across every
  // section and remember the first (sectionId, fieldId) pair in catalog
  // order so "Continue →" can jump there.
  const readiness = useMemo(() => {
    let unfilledCount = 0;
    let nextSectionId: string | null = null;
    let nextFieldId: FieldId | null = null;
    for (const sectionId of ORDERED_SECTIONS) {
      const progress = sectionProgress.get(sectionId);
      if (!progress) continue;
      unfilledCount += progress.missingFieldIds.length;
      if (nextFieldId === null && progress.missingFieldIds.length > 0) {
        nextSectionId = sectionId;
        nextFieldId = progress.missingFieldIds[0];
      }
    }
    return { unfilledCount, nextSectionId, nextFieldId };
  }, [sectionProgress]);

  // Live applicant-name echo for the top bar.
  const applicantFirst = readText(engine, FIRST_NAME_FIELD.id);
  const applicantLast = readText(engine, LAST_NAME_FIELD.id);
  const applicantName =
    applicantFirst || applicantLast
      ? `${applicantFirst} ${applicantLast}`.trim()
      : 'New application';

  const currentIndex = ORDERED_SECTIONS.indexOf(currentSectionId);
  const goPrev = (): void => {
    if (currentIndex > 0) {
      setCurrentSectionId(ORDERED_SECTIONS[currentIndex - 1]);
    }
  };
  const goNext = (): void => {
    if (currentIndex >= 0 && currentIndex < ORDERED_SECTIONS.length - 1) {
      setCurrentSectionId(ORDERED_SECTIONS[currentIndex + 1]);
    }
  };

  /**
   * Pre-fill banner action. SectionRenderer fires
   * this when the user clicks "Confirm and continue" in the banner.
   * We mark the section complete (which validates that nothing is
   * missing — the banner only shows when missingFieldIds is empty, so
   * the guard inside `markSectionComplete` no-ops the touched-flush
   * branch) and advance to the next section in `ORDERED_SECTIONS`.
   */
  const handleConfirmAndContinue = (sectionId: string): void => {
    markSectionComplete(sectionId);
    goNext();
  };

  // Render a parent review panel for the
  // `__review_*` pseudo-section. Builds the visible-fields-by-section
  // map from the engine's per-field visibility, then hands the panel
  // a stringifier for answer values. The panel itself owns layout +
  // gap rendering.
  const renderReviewPanel = (parentId: string): JSX.Element | null => {
    const parentGroup = PARENT_GROUPS.find(g => g.id === parentId);
    if (!parentGroup) return null;
    const parentSectionIds: readonly string[] = parentGroup.subHeadings
      ? parentGroup.subHeadings.flatMap(s => Array.from(s.sectionIds))
      : Array.from(parentGroup.sectionIds ?? []);

    const visibleFieldsBySection = new Map<string, FieldDefinition[]>();
    for (const sectionId of parentSectionIds) {
      // Phase 7i — use the sectionId index (O(1) lookup) instead of
      // an O(N=580) catalog scan per parent section.
      const visible = getFieldsForSection(sectionId).filter(
        f =>
          // Skip repeatable-group fields — engine.getAnswer requires
          // a groupInstanceId for them, and structured per-row
          // rendering is deferred to a Phase 7c polish pass.
          f.repeatableGroupId === undefined &&
          engine.getFieldStatus(f.id).visible,
      );
      visibleFieldsBySection.set(sectionId, visible);
    }

    return (
      <ParentReviewPanel
        parentGroupId={parentGroup.id}
        parentLabel={
          parentGroup.summaryLeaf?.label ?? `Review ${parentGroup.label}`
        }
        sectionIds={parentSectionIds}
        getSectionProgress={sectionId =>
          sectionProgress.get(sectionId) ?? undefined
        }
        getAnswerForField={fieldId => {
          const field = fieldsById.get(fieldId);
          return stringifyAnswer(engine.getAnswer(fieldId), {
            field,
            resolveOptionLabel: (f, value) => {
              const opt = resolveFieldOptions(f).find(o => o.value === value);
              return opt?.label ?? value;
            },
          });
        }}
        fieldsBySectionId={visibleFieldsBySection}
        onJumpToField={(sectionId, fieldId) => {
          setCurrentSectionId(sectionId);
          // Best-effort focus: queue a microtask so the section renders
          // before we look for the field's data attribute.
          queueMicrotask(() => {
            const el = document.querySelector<HTMLElement>(
              `[data-field-id="${fieldId}"]`,
            );
            el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const focusable = el?.querySelector<HTMLElement>(
              'input, select, textarea, button',
            );
            focusable?.focus();
          });
        }}
        onEditSection={sectionId => setCurrentSectionId(sectionId)}
      />
    );
  };

  // The signatures section appends a custom panel below the
  // catalog-driven section. For paying customers it surfaces a
  // reassurance that the case team will assemble and deliver the
  // packet — they MUST NOT be able to download filled PDFs
  // themselves, that's the deliverable they pay us for. Internal
  // testers (provisioned via the `tester: true` custom claim) keep
  // the Download buttons so QA can spot-check engine output
  // end-to-end.
  const renderSignatures = (): JSX.Element => {
    if (props.isTester !== true) {
      return (
        <section
          aria-label="Signatures"
          className={styles.shell__section}
        >
          <p className={styles.shell__section_intro}>
            Your filing packet will be assembled by our case team and
            delivered once it's ready — there's nothing for you to
            download here. Make sure your answers above are accurate
            and complete, then submit your case for review.
          </p>
        </section>
      );
    }
    return (
      <section
        aria-label="Signatures downloads"
        className={styles.shell__section}
      >
        <p className={styles.shell__section_intro}>
          Download a filled copy of each form for your records. Buttons
          unlock as the relevant questions are answered. The case team
          uses the same answers when they assemble your final packet —
          these downloads are your reference.
        </p>
        <button
          type="button"
          className={styles.shell__download_button}
          disabled={
            !hasAnyAnswer ||
            downloadApi.status.kind === 'downloading' ||
            downloadApi.status.kind === 'loading_engine'
          }
          onClick={() => {
            void downloadApi.download('i-485');
          }}
        >
          {downloadApi.status.kind === 'loading_engine' &&
          downloadApi.status.formId === 'i-485'
            ? 'Preparing your I-485…'
            : downloadApi.status.kind === 'downloading' &&
              downloadApi.status.formId === 'i-485'
            ? 'Generating your I-485…'
            : 'Download your I-485'}
        </button>{' '}
        <button
          type="button"
          className={styles.shell__download_button}
          disabled={
            !hasSponsorPhone ||
            downloadApi.status.kind === 'downloading' ||
            downloadApi.status.kind === 'loading_engine'
          }
          onClick={() => {
            void downloadApi.download('i-864');
          }}
        >
          {downloadApi.status.kind === 'loading_engine' &&
          downloadApi.status.formId === 'i-864'
            ? 'Preparing your I-864…'
            : downloadApi.status.kind === 'downloading' &&
              downloadApi.status.formId === 'i-864'
            ? 'Generating your I-864…'
            : 'Download your I-864'}
        </button>
        <DownloadStatusPanel status={downloadApi.status} />
      </section>
    );
  };

  // Phase 8 (applicant-web slice) — once the case team is driving,
  // swap the editable intake for a clean status surface. Legacy
  // applications without a caseState field default to `'filling'` so
  // a missing field never bumps an existing applicant out of their
  // in-progress session.
  const resolvedCaseState = defaultCaseStateForUnknown(props.caseState);
  if (isPostSubmitState(resolvedCaseState)) {
    return (
      <PostSubmitView
        caseState={resolvedCaseState}
        submittedAtMs={
          props.submittedAtMs !== undefined && props.submittedAtMs !== null
            ? props.submittedAtMs
            : Date.now()
        }
        shippedAtMs={props.shippedAtMs ?? null}
        trackingNumber={props.trackingNumber ?? null}
        packetStoragePath={props.packetStoragePath}
        packetFileName={props.packetFileName}
        packetSizeBytes={props.packetSizeBytes}
        onDownloadPacket={props.onDownloadPacket}
      />
    );
  }

  // Render a layout-mimicking skeleton
  // while the engine hydrates from the persistence adapter. Without
  // this, the first paint shows fully-empty fields on a white card
  // for the half-second-or-so the Firestore round-trip takes — which
  // reads as "is anything happening?"
  //
  // Defer the skeleton via a short
  // timer so fast hydrations (sync adapters in tests, sync localStorage
  // in prod) never see a 1-frame skeleton flash. Slow hydrations
  // (Firestore round-trip) still get the skeleton after the timer
  // fires. 80ms is the threshold at which a "loading flash" starts
  // feeling laggy to the user; under it, the page flips straight to
  // the real UI.
  if (!hasHydrated && showHydrateSkeleton) {
    return <HydrateSkeletonView />;
  }

  return (
    <main id="main-content" className={styles.shell}>
      <div className={styles.shell__topbar}>
        {/* Brand + page title pairing. The Greenbroad wordmark
         * anchors the surface to the company; "Your application"
         * grounds the user in what this page is. The img is
         * positioned absolutely-via-grid on wide viewports and
         * stacks above the H1 on mobile via the existing flex
         * fallback on .shell__topbar. */}
        <div className={styles.shell__title_group}>
          <img
            src="/brand/greenbroad-wordmark.png"
            alt="Greenbroad"
            className={styles.shell__brand}
            width={140}
            height={29}
          />
          <h1 className={styles.shell__title}>Your application</h1>
        </div>
        <div className={styles.shell__topbar_meta}>
          <span aria-label="Applicant name">{applicantName}</span>
          {/*
            Phase 7e — auto-save reassurance. The persistence layer
            already saves on every keystroke (debounced 500 ms), but
            applicants don't see that — the absence of a "Save"
            button reads as missing rather than implicit. This small
            secondary line surfaces the guarantee. The wrapper is
            always rendered so screen readers find a stable
            `role="status"` to anchor announcements; the message is
            empty until the first persist completes.
          */}
          <span
            className={styles.shell__topbar_autosave}
            role="status"
            aria-live="polite"
            aria-label="Auto-save status"
          >
            {/*
              Wave 1 Slice 4 (2026-05-02) — render the green check +
              "Saved · {time}" inline. The check is aria-hidden because
              the role="status" wrapper already announces the wording
              to AT; the icon is purely visual reassurance. Prior to
              first save (lastSavedAt === null), the wrapper renders
              empty so the live region has stable identity but doesn't
              announce a misleading "saved" before any save happened.
            */}
            {lastSavedAt !== null ? (
              <>
                {/* 2026-05-24 — collapsed "Saved · 5 minutes ago" to
                 *   a quiet check icon with the timestamp moved to the
                 *   tooltip / aria-label. The persistent text chip in
                 *   the header read as a status announcement competing
                 *   with the readiness chip next to it; visual reassurance
                 *   that "things saved" is enough. */}
                <span
                  aria-hidden="true"
                  className={styles.shell__topbar_autosave_icon}
                  title={`Saved · ${formatRelativeSave(lastSavedAt, nowMs)}`}
                >
                  ✓
                </span>
                <span className="sr-only">
                  Saved · {formatRelativeSave(lastSavedAt, nowMs)}
                </span>
              </>
            ) : null}
          </span>
          {/*
            Phase 7-IA Slice 8 (a11y audit H3) — single persistent
            `role="status"` wrapper. The previous code rendered two
            sibling `<span role="status">` elements and swapped between
            them on `unfilledCount` flip; even when React reused the
            same DOM node via type matching, AT engines are
            inconsistent about announcing live-region content changes
            on conditionally-rendered branches. A single wrapper with
            stable role + explicit `aria-live="polite"` is the
            audit-recommended shape.
          */}
          <span
            className={styles.shell__topbar_readiness}
            role="status"
            aria-live="polite"
            aria-label="Readiness"
          >
            {readiness.unfilledCount > 0 ? (
              <>
                <span aria-hidden="true">○</span>
                <span>
                  {readiness.unfilledCount} remaining
                </span>
                <button
                  type="button"
                  className={styles.shell__topbar_continue}
                  onClick={() => {
                    if (readiness.nextSectionId !== null) {
                      setCurrentSectionId(readiness.nextSectionId);
                      // Best-effort focus: queue a microtask so the
                      // section renders before we look for the field.
                      // Today no primitive emits `data-field-id`, so
                      // the optional chains no-op gracefully —
                      // section navigation alone is the must-have.
                      if (readiness.nextFieldId !== null) {
                        const fieldId = readiness.nextFieldId;
                        queueMicrotask(() => {
                          const el = document.querySelector<HTMLElement>(
                            `[data-field-id="${fieldId}"]`,
                          );
                          el?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                          });
                          const focusable = el?.querySelector<HTMLElement>(
                            'input, select, textarea, button',
                          );
                          focusable?.focus();
                        });
                      }
                    }
                  }}
                >
                  Continue →
                </button>
              </>
            ) : (
              <>
                <span aria-hidden="true">✓</span>
                {/* Wave 2 Slice 7 (2026-05-02) — "Ready to submit"
                 * → "Looks ready" reads as a thumbs-up rather than a
                 * directive. The CTA still says "Review & submit →"
                 * so the action stays unambiguous. */}
                <span>Looks ready</span>
                <button
                  type="button"
                  className={styles.shell__topbar_continue}
                  onClick={() => setCurrentSectionId('signatures')}
                >
                  Review & submit →
                </button>
              </>
            )}
          </span>
          {props.topBarSlot}
        </div>
      </div>

      <div className={styles.shell__layout}>
        <SectionProgressTree
          parentGroups={parentGroupViews}
          currentSectionId={currentSectionId}
          onSectionClick={setCurrentSectionId}
          ariaLabel="Intake sections"
          visitedSectionIds={visitedSectionIds}
        />

        <div className={styles.shell__main}>
          {hydrateError !== null ? (
            <div
              className={styles.shell__hydrate_error}
              role="alert"
              data-hydrate-error
            >
              <div className={styles.shell__hydrate_error_message}>
                <strong>Couldn't load your saved work.</strong> Your
                edits aren't being auto-saved right now. Check your
                connection and try again.
              </div>
              <button
                type="button"
                className={styles.shell__hydrate_error_retry}
                onClick={() => {
                  setHydrateError(null);
                  setHasHydrated(false);
                  setHydrateAttempt(n => n + 1);
                }}
              >
                Retry
              </button>
            </div>
          ) : null}
          {peerCount > 0 && !isPrimary ? (
            <div
              className={styles.shell__secondary_tab}
              role="status"
              data-secondary-tab
            >
              <strong>This application is open in another tab.</strong>{' '}
              Auto-save is paused here to keep that tab's edits safe.
              Close this tab or switch to the other one to continue
              editing.
            </div>
          ) : null}
          {/*
            Phase 7e — "Welcome back" banner for returning applicants.
            Shown when the engine has at least one answered field, the
            user hasn't dismissed the banner this session, and we're
            not on a review-leaf or the terminal signatures section
            (banners at the end of the flow read as nagging). The
            banner reads `currentSectionId` to name the section the
            applicant is resuming on — the storage-driven initial
            state from above is what makes "where you left off"
            literal rather than aspirational.
          */}
          {hasAnyAnswer &&
          !welcomeBackDismissed &&
          !isSummaryLeaf &&
          currentSectionId !== 'signatures' ? (
            <WelcomeBackBanner
              firstName={applicantFirst}
              currentSectionLabel={getSectionDisplayLabel(currentSectionId)}
              onDismiss={() => setWelcomeBackDismissed(true)}
            />
          ) : null}
          {isSummaryLeaf ? renderReviewPanel(summaryLeafParentId!) : (
            <>
              <SectionValidationSummary
                progress={sectionProgress.get(currentSectionId) ?? null}
                fieldsById={fieldsById}
                isComplete={completedSections.has(currentSectionId)}
                onMarkComplete={() => markSectionComplete(currentSectionId)}
              />
              {/*
                Wave 1 Slice 2 (2026-05-02) — wrap the SectionRenderer in
                a card surface so the section reads as a contained
                experience instead of bare fields floating on the page
                background. The wrapper also styles SectionRenderer's
                `<h2>` (via `.shell__main_card > section > h2` in the
                module CSS) so the heading carries real visual weight
                without SectionRenderer having to opt into a className.
              */}
              <div className={styles.shell__main_card}>
                <SectionRenderer
                  sectionId={currentSectionId}
                  engine={engine}
                  touchedFieldIds={touchedFieldIds}
                  onTouchKey={markFieldTouched}
                  repeatableInstancesByGroup={instancesByGroup}
                  onAddInstance={onAddInstance}
                  onRemoveInstance={onRemoveInstance}
                  uploadDocument={props.uploadDocument}
                  onUploadError={(fieldId, message) => {
                    setUploadError(message);
                    // Document upload failures were previously
                    // user-visible only (the inline banner) — now also
                    // route to Sentry so a recurring quota / Firestore-
                    // permission issue surfaces in the Issues feed
                    // instead of dying with the toast.
                    reportError(new Error(message), {
                      kind: 'document_upload',
                      fieldId,
                    });
                  }}
                  onConfirmAndContinue={handleConfirmAndContinue}
                  isSectionComplete={completedSections.has(currentSectionId)}
                  submitForReview={
                    props.onSubmitForReview !== undefined
                      ? {
                          applicationId,
                          onSubmit: props.onSubmitForReview,
                        }
                      : undefined
                  }
                />
              </div>
              {uploadError !== null ? (
                <p role="alert" className={styles.shell__upload_error}>
                  Upload failed: {uploadError}
                </p>
              ) : null}
              {currentSectionId === 'signatures' ? renderSignatures() : null}

              {/* Hide Prev/Next on the review leaf — the user navigates
                  via the tree, and the leaf isn't part of the linear
                  ORDERED_SECTIONS sequence. */}
              <div className={styles.shell__nav_buttons}>
                {/* Wave 2 Slice 7 (2026-05-02) — "Previous section" /
                 * "Next section" → the shorter "Back" / "Continue" with
                 * arrows. The relationship to "section" is already
                 * obvious from the button's position under the section
                 * card; spelling it out reads as bureaucratic. */}
                <button
                  type="button"
                  className={styles.shell__nav_button}
                  onClick={goPrev}
                  disabled={currentIndex <= 0}
                >
                  ← Back
                </button>
                <button
                  type="button"
                  className={`${styles.shell__nav_button} ${styles['shell__nav_button--primary']}`}
                  onClick={goNext}
                  disabled={currentIndex >= ORDERED_SECTIONS.length - 1}
                >
                  Continue →
                </button>
              </div>

              {/*
                Wave 3 Slice 8 (2026-05-02) — pacing affordance. A tiny
                "Section X of Y · Up next: {label}" line under the nav
                buttons tells the applicant where they are in the linear
                flow and what the Continue button will jump to. The
                last visible section gets a different message ("Almost
                done — next is review & submit") so the affordance
                still adds value at the end. Hidden on the review-leaf
                rendering branch (this whole block sits inside the
                non-summary-leaf else).
              */}
              {(() => {
                const totalSections = ORDERED_SECTIONS.length;
                if (currentIndex < 0) return null;
                const isLast = currentIndex >= totalSections - 1;
                const nextSectionId = isLast
                  ? null
                  : ORDERED_SECTIONS[currentIndex + 1];
                const positionLabel = `Section ${currentIndex + 1} of ${totalSections}`;
                if (nextSectionId === null) {
                  return (
                    <p className={styles.shell__next_peek}>
                      <span className={styles.shell__next_peek_position}>
                        {positionLabel}
                      </span>
                      <span className={styles.shell__next_peek_dot} aria-hidden="true">
                        ·
                      </span>
                      <span>Almost done — review & submit is next.</span>
                    </p>
                  );
                }
                return (
                  <p className={styles.shell__next_peek}>
                    <span className={styles.shell__next_peek_position}>
                      {positionLabel}
                    </span>
                    <span className={styles.shell__next_peek_dot} aria-hidden="true">
                      ·
                    </span>
                    <span>
                      Up next:{' '}
                      <span className={styles.shell__next_peek_section}>
                        {getSectionDisplayLabel(nextSectionId)}
                      </span>
                    </span>
                  </p>
                );
              })()}
            </>
          )}

          <AuditPanel engine={engine} />
        </div>
      </div>
      {/*
        Slice 9.5 (2026-05-17) — persistent UPL boundary footer per
        ADR 0022 §"Things that follow from this decision" action item
        3. Keeps the "you are the filer, this is a tool not a law
        firm" framing visible on every form page. Tiny + neutral so
        it doesn't compete with form copy; persistent so a user
        deep in the flow can't miss it.
      */}
      <footer className={styles.shell__legal_footer} role="contentinfo">
        <span>
          <strong>Greenbroad is a document-preparation tool.</strong>{' '}
          You are the filer and signer. We do not provide legal
          advice or file forms on your behalf. If your case is
          complex (criminal history, prior removal, prior
          misrepresentation), consult an immigration attorney
          before mailing the packet.
        </span>
        <span className={styles.shell__legal_footer_support}>
          {/* 2026-05-25 — in-product support contact. Plain mailto for
              v1; can be wired to an intercom / front widget later. */}
          Need help?{' '}
          <a
            href="mailto:support@greenbroad.co?subject=AOS%20app%20support"
            className={styles.shell__legal_footer_link}
          >
            Email support
          </a>
        </span>
      </footer>
    </main>
  );
}

/**
 * Hydrate skeleton.
 *
 * Renders a layout-mimicking placeholder while the engine resolves
 * answers from the persistence adapter. Mirrors the top-bar / tree /
 * main-card geometry so the user sees the shell take shape, not a
 * blank page. The shimmer animation respects `prefers-reduced-motion`.
 *
 * Kept inline (rather than a separate file) because it's tightly
 * coupled to the App's CSS module — the skeleton classes live in
 * App.module.css alongside the real shell rules so any layout
 * adjustment to the shell can keep the placeholders in sync.
 */
function HydrateSkeletonView(): JSX.Element {
  const treeRowKeys = ['a', 'b', 'c', 'd', 'e'];
  const fieldKeys = ['1', '2', '3'];
  return (
    <main
      id="main-content"
      className={styles.shell}
      aria-busy="true"
      aria-label="Loading your application"
    >
      <div className={styles.shell__topbar}>
        <div
          className={`${styles.skeleton_block} ${styles.skeleton_title}`}
        />
        <div
          className={`${styles.skeleton_block} ${styles.skeleton_meta}`}
        />
      </div>
      <div className={styles.shell__layout}>
        <nav className={styles.skeleton_tree} aria-hidden="true">
          {treeRowKeys.map(k => (
            <div
              key={k}
              className={`${styles.skeleton_block} ${styles.skeleton_tree_row}`}
            />
          ))}
        </nav>
        <div className={styles.shell__main}>
          <div className={styles.shell__main_card}>
            <div
              className={`${styles.skeleton_block} ${styles.skeleton_heading}`}
            />
            <div
              className={`${styles.skeleton_block} ${styles.skeleton_intro}`}
            />
            {fieldKeys.map(k => (
              <div key={k}>
                <div
                  className={`${styles.skeleton_block} ${styles.skeleton_label}`}
                />
                <div
                  className={`${styles.skeleton_block} ${styles.skeleton_input}`}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}

/**
 * Inline status panel for the download flow. Renders nothing when
 * idle so the layout doesn't reserve empty space; otherwise shows
 * a status line and the diagnostics list (when the engine emitted
 * any). All status changes share an `aria-live="polite"` region
 * so screen readers announce them.
 */
function DownloadStatusPanel({
  status,
}: {
  status: DownloadStatus;
}): JSX.Element | null {
  if (status.kind === 'idle') return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={styles.shell__download_status}
    >
      {status.kind === 'loading_engine' && (
        <p>
          Loading PDF engine for {status.formId.toUpperCase()}… (one-time
          per session)
        </p>
      )}
      {status.kind === 'downloading' && (
        <p>Generating filled {status.formId.toUpperCase()} PDF…</p>
      )}
      {status.kind === 'success' && (
        <>
          <p>
            Downloaded {status.formId.toUpperCase()} (
            {status.diagnostics.length} diagnostic
            {status.diagnostics.length === 1 ? '' : 's'}).
          </p>
          {status.diagnostics.length > 0 && (
            <details className={styles.shell__download_details}>
              <summary>Diagnostics</summary>
              <ul className={styles.shell__download_diag_list}>
                {status.diagnostics.map((d, i) => (
                  <li key={`${d.code}-${d.fieldId}-${d.widgetBasename}-${i}`}>
                    <code>{d.code}</code> — {d.fieldId || '(packet)'}{' '}
                    <em>{d.widgetBasename || ''}</em>: {d.message}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
      {status.kind === 'error' && (
        <p>
          Failed to generate {status.formId.toUpperCase()}: {status.message}
        </p>
      )}
    </div>
  );
}
